import type { EnterpriseTenantType, TypeCorrectionPreflight } from "@closer/shared";
import { ENTERPRISE_TENANT_TYPES, TENANT_TYPE_NAMES } from "@closer/shared";
import { Button, Card, FormField, Modal, Select, Spinner, Textarea, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, ApiRequestError } from "../../lib/apiClient";
import { tenantKey } from "../../lib/tenantQuery";

/**
 * ORGANİZASYON TÜRÜ DÜZELTMESİ (CLOS-812).
 *
 * ### ⚠ Bu bir dropdown DEĞİL
 *
 * Kart açıkça yasaklıyor: *"Organization Profile içinde sıradan COMPANY <-> LAW_FIRM
 * dropdown yok."* Tür profil formunun bir alanı olsaydı, adres düzelten bir kaydetme
 * organizasyonun kimliğini de değiştirebilirdi. Bu yüzden düzeltme AYRI bir kartta, ayrı
 * bir pencerede, gerekçeyle ve ön kontrolle yürüyor.
 *
 * ### ⚠ Ön kontrol pencere AÇILIRKEN çekiliyor
 *
 * Etki karar verilmeden önce görülmeli. Düzeltme yanıtında dönseydi yönetici neyin
 * engellediğini ancak reddedildikten sonra öğrenirdi — kapatma ön izlemesiyle aynı gerekçe.
 *
 * ### ⚠ Engeller SAYIYLA gösteriliyor
 *
 * "Düzeltilemez" demek arızayı yokluk gibi gösterirdi. Hangi domainde kaç kayıt olduğu
 * yazıyor; yönetici düzeltmenin neden mümkün olmadığını görüyor.
 */
export function OrganizationTypeCorrectionCard({
  tenantId,
  currentType,
}: {
  tenantId: string;
  currentType: EnterpriseTenantType;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [acik, setAcik] = useState(false);
  const [hedef, setHedef] = useState<EnterpriseTenantType>(
    currentType === "enterprise_company" ? "enterprise_law_firm" : "enterprise_company",
  );
  const [gerekce, setGerekce] = useState("");

  const onKontrol = useQuery({
    queryKey: tenantKey(tenantId, "tur-duzeltme-onkontrol"),
    queryFn: () =>
      api<TypeCorrectionPreflight>(`/api/tenants/${tenantId}/type-correction/preflight`, {
        tenantId,
      }).then((r) => r.data),
    // Yalnız pencere açıkken: doluluk haritası, sayfa her açılışta çekilecek bir veri değil.
    enabled: acik,
  });

  const duzelt = useMutation({
    mutationFn: async () =>
      (
        await api<{ type: EnterpriseTenantType; changed: boolean }>(
          `/api/tenants/${tenantId}/type-correction`,
          {
            method: "POST",
            tenantId,
            body: JSON.stringify({ type: hedef, reason: gerekce }),
          },
        )
      ).data,
    onSuccess: async (sonuc) => {
      setAcik(false);
      setGerekce("");
      /**
       * ⚠ BAĞLAM, ÜYELİK LİSTESİ ve PROFİL birlikte tazeleniyor. Tür yetenek kümesini
       * belirliyor: yalnız profili tazeleseydik menü eski türün yeteneklerini göstermeye
       * devam eder, kullanıcı kapalı bir domaini açık görürdü (CLOS-814).
       */
      await queryClient.invalidateQueries({ queryKey: ["memberships"] });
      await queryClient.refetchQueries({ queryKey: tenantKey(tenantId, "context") });
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "organization-profile"),
      });
      toast.success(
        sonuc.changed
          ? `Organizasyon türü düzeltildi: ${TENANT_TYPE_NAMES[sonuc.type]}`
          : "Organizasyon zaten bu türde; değişiklik yapılmadı",
      );
    },
    onError: (hata: unknown) =>
      toast.error(
        hata instanceof ApiRequestError ? hata.message : "Organizasyon türü düzeltilemedi",
      ),
  });

  const engelli = onKontrol.data ? !onKontrol.data.correctable : false;

  return (
    <>
      <Card title="Organizasyon türü düzeltmesi">
        <div className="flex flex-col gap-3">
          <p className="text-sm text-fg-muted">
            Tür oluşturma sırasında belirlenir ve profil düzenlemesiyle değişmez. Yanlış türle
            açılmış, henüz kullanılmamış bir organizasyon burada düzeltilebilir.
          </p>
          <div>
            <Button variant="secondary" size="sm" onClick={() => setAcik(true)}>
              Türü düzelt
            </Button>
          </div>
        </div>
      </Card>

      <Modal open={acik} onClose={() => setAcik(false)} title="Organizasyon türünü düzelt">
        {onKontrol.isPending ? (
          <Spinner />
        ) : (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-fg-muted">
              Mevcut tür:{" "}
              <span className="font-medium text-fg">{TENANT_TYPE_NAMES[currentType]}</span>
            </p>

            {engelli ? (
              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium text-fg">
                  Bu organizasyon kullanılmaya başlanmış; türü düzeltilemez.
                </p>
                <ul className="flex flex-col gap-1 text-sm text-fg-muted">
                  {onKontrol.data!.blockers.map((engel) => (
                    <li key={engel.domain}>
                      {engel.label}: <span className="font-medium text-fg">{engel.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <>
                <FormField label="Yeni tür">
                  <Select
                    id="tur-duzeltme-hedef"
                    value={hedef}
                    onChange={(e) => setHedef(e.target.value as EnterpriseTenantType)}
                  >
                    {ENTERPRISE_TENANT_TYPES.map((tur) => (
                      <option key={tur} value={tur}>
                        {TENANT_TYPE_NAMES[tur]}
                      </option>
                    ))}
                  </Select>
                </FormField>

                <FormField label="Düzeltme gerekçesi">
                  <Textarea
                    id="tur-duzeltme-gerekce"
                    value={gerekce}
                    onChange={(e) => setGerekce(e.target.value)}
                    rows={3}
                  />
                </FormField>

                <p className="text-sm text-fg-muted">
                  Düzeltme denetim kaydına gerekçesiyle birlikte yazılır ve rol yetkileri yeni türe
                  göre yeniden hesaplanır.
                </p>

                <div className="flex flex-wrap gap-2">
                  <Button loading={duzelt.isPending} onClick={() => duzelt.mutate()}>
                    Türü düzelt
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={duzelt.isPending}
                    onClick={() => setAcik(false)}
                  >
                    Vazgeç
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
